import React, { FunctionComponent } from "react";
import { rgba } from "polished";
import styled from "styled-components";
import { useEditMode } from "../../contexts/edit";
import { SectionOrderButtons } from "../edit-controls/section-order-buttons";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";

interface ContentGridProps {
  className?: string;
  title: string;
  children: React.ReactNode;
  renderAction?: () => React.ReactNode;
  noEditUI?: boolean;
  sectionId?: SectionIdentifier;
}

export const ContentGrid: FunctionComponent<ContentGridProps> = ({
  className,
  title,
  children,
  renderAction,
  noEditUI = false,
  sectionId,
}) => {
  const { editMode } = useEditMode();

  return (
    <ContentWrapper
      editMode={!noEditUI && editMode}
      className={className || ""}
    >
      <TitleWrapper>
        <h3 className="title">{title}</h3>
        {!noEditUI && editMode
          ? sectionId && <SectionOrderButtons sectionId={sectionId} />
          : renderAction?.()}
      </TitleWrapper>
      {children}
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div<{ editMode: boolean }>`
  border: ${({ editMode, theme }) =>
    editMode ? `1px solid ${rgba(theme.colors.primary, 0.1)}` : "inherit"};
  padding: ${({ editMode }) => (editMode ? "0 24px 24px 24px" : "0")};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-block-end: 20px;

  @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
    margin-block-end: 24px;
    margin-inline: 24px;
  }

  .title,
  .link {
    font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.primary};
  }

  .link {
    position: relative;
    opacity: 0.5;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:after {
      content: "";
      border-radius: 2px;
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 100%;
      width: 0;
      height: 2px;
      background-color: ${({ theme }) => theme.colors.primary};
      opacity: 0;
      transition: opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
        right
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
        width
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    &:hover,
    &:focus,
    &:focus-within {
      opacity: 1;
    }
    &:focus,
    &:focus-within {
      outline: none;
      &:after {
        opacity: 1;
        right: 0;
        width: 100%;
      }
    }
  }
`;
